import type {
  FormattedNewWager,
  FormattedNewWagerAttributes,
  FormattedNewWagerHeader,
  FormattedNewWagerLineup,
  FormattedNewWagerLineupDetails,
  FormattedNewWagerPromotion,
  FormattedNewWagers,
  FormattedNewWagersResponse,
  NewWager,
  NewWagerDateRangeFilter,
  NewWagerIdType,
  NewWagerIncluded,
  NewWagerIncludedAttributes,
  NewWagerLineupGameType,
  NewWagerModalDetails,
  NewWagerPlayerStatus,
  NewWagerPlayerStatusDisplay,
  NewWagerPromotionType,
  NewWagerResultStatus,
  NewWagerWagerType,
  NewWagersResponse
} from '@library/types/newWagers';
import {
  NEW_WAGER_MODAL_DETAILS,
  PLAYER_PLACEHOLDER_IMAGE_URL
} from '@bo-constants/newWagers';
import { DATE_TIME_FORMAT } from '@bo-constants/member/members';
import { DateTime } from 'luxon';
import { MEMBER_DETAILS_CHILD_PATHS } from '@bo-constants/routes';
import { convertCentsToDollars } from '@library/utils/payments';
import { dateDisplay } from '@bo-utils/members/memberCommon';
import dnpBadge from '@library/assets/images/icons/status-badges/dnp-badge.svg';
import hitBadge from '@library/assets/images/icons/status-badges/hit-badge.svg';
import missBadge from '@library/assets/images/icons/status-badges/miss-badge.svg';
import notStartedBadge from '@library/assets/images/icons/status-badges/not-started-badge.svg';
import rebootBadge from '@library/assets/images/icons/status-badges/reboot-badge.svg';
import { removeUnderscore } from '@bo-utils/common';
import tieBadge from '@library/assets/images/icons/status-badges/tie-badge.svg';
import { v4 as uuid } from 'uuid';

export const hasNewWagerGameStarted = (
  startTime?: string | null,
  status?: string | null
): boolean => {
  const now = DateTime.now();
  const hasStarted =
    DateTime.fromISO(startTime ?? now.toISO()) <= now || status !== 'pre_game';
  return hasStarted;
};

export const isNewWagerGameLive = (
  startTime?: string,
  status?: string,
  isFinal?: boolean,
  isOffTheBoard?: boolean
): boolean =>
  hasNewWagerGameStarted(startTime, status) && !isFinal && !isOffTheBoard;

export function isCorrectPrediction(
  actualScore: string,
  lineScore: string,
  wagerType?: NewWagerWagerType
): boolean {
  const actualScoreNum = Number(actualScore);
  const lineScoreNum = Number(lineScore);

  if (wagerType === 'under' && actualScoreNum < lineScoreNum) {
    return true;
  }
  if (wagerType === 'over' && actualScoreNum > lineScoreNum) {
    return true;
  }

  return false;
}

export const setNewWagerWagerType = (
  wagerType: string | undefined
): NewWagerWagerType | undefined => {
  if (wagerType === 'over' || wagerType === 'under') {
    return wagerType as NewWagerWagerType;
  }

  return undefined;
};

export const getNewWagerPlayerStatus = (
  prediction: NewWagerIncludedAttributes,
  projection: NewWagerIncludedAttributes,
  score: NewWagerIncludedAttributes
): NewWagerPlayerStatus => {
  const actualScore = String(score.score);
  const expectedScore = String(prediction.line_score);
  const wagerType = setNewWagerWagerType(prediction.wager_type);
  const hasGameStarted = hasNewWagerGameStarted(
    projection.start_time,
    projection.status
  );

  if (!hasGameStarted) {
    return 'pre_game';
  }

  const isFinalScore = score.is_final;
  const isHitting = isCorrectPrediction(actualScore, expectedScore, wagerType);
  const isPredictionCorrect = isFinalScore && isHitting;
  const isPredictionTie = isFinalScore && actualScore === expectedScore;
  const isPredictionIncorrect = isFinalScore && !isHitting && !isPredictionTie;
  const isPlayerDnped = score.is_off_the_board;
  const isPlayerRebooted =
    score.unders_win_dnp && wagerType === 'over' && !isHitting;

  if (isPlayerDnped) {
    return 'dnp';
  }

  if (isPredictionTie) {
    return 'tie';
  }

  if (isPredictionCorrect) {
    return 'hit';
  }

  if (isFinalScore && isPlayerRebooted) {
    return 'reboot';
  }

  if (isPredictionIncorrect) {
    return 'miss';
  }

  if (isHitting) {
    return 'hitting';
  }

  if (actualScore === expectedScore) {
    return 'tying';
  }

  if (!isHitting) {
    return 'missing';
  }

  return 'in_progress';
};

export const getNewWagerLineupGameType = (
  gameMode: string | undefined,
  pickProtection: boolean
): NewWagerLineupGameType => {
  if (gameMode === 'f2p_streak') {
    return 'Play';
  }

  if (gameMode === 'squads') {
    return 'Entry Fee';
  }

  if (gameMode === 'prizepools') {
    return 'Arena Play';
  }

  return pickProtection ? 'Flex Play' : 'Power Play';
};

export const getNewWagerLineup = (
  predictionsData: Array<NewWagerIdType> | null,
  included: Array<NewWagerIncluded>
): FormattedNewWagerLineup => {
  const findIncludedData = (inclId: string | undefined, inclType: string) => {
    const includedItem = included?.find(
      ({ id, type }) => id === inclId && type === inclType
    );
    const { attributes, relationships } = includedItem ?? {};
    return { attributes, relationships };
  };

  // Create lineups from projections, new_players, and leagues data from included
  const lineup = predictionsData?.map((prediction) => {
    // Prediction data from included
    const { attributes: predictionAttr, relationships: predictionRel } =
      findIncludedData(prediction.id, 'prediction');

    // Projection data from included
    const projectionId = predictionRel?.projection?.data?.id;
    const { attributes: projectionAttr, relationships: projectionRel } =
      findIncludedData(projectionId, 'projection');
    const wagerType = setNewWagerWagerType(predictionAttr?.wager_type);

    // Score data from included
    const scoreId = projectionRel?.score?.data?.id;
    const scoreAttr = findIncludedData(scoreId, 'score').attributes;

    // New player data from included
    const newPlayerId = projectionRel?.new_player?.data?.id;
    const playerAttr = findIncludedData(newPlayerId, 'new_player').attributes;

    // League data from included
    const leagueId = projectionRel?.league?.data?.id;
    const leagueAttr = findIncludedData(leagueId, 'league').attributes;

    // Stat Type from included
    const statTypeId = projectionRel?.stat_type?.data?.id;
    const statTypeAttr = findIncludedData(statTypeId, 'stat_type').attributes;

    // Player status
    const validIncludeAttributes =
      predictionAttr && projectionAttr && scoreAttr;
    const playerStatus = validIncludeAttributes
      ? getNewWagerPlayerStatus(predictionAttr, projectionAttr, scoreAttr)
      : 'pre_game';

    return {
      id: uuid(),
      isPromo: predictionAttr?.is_promo ?? false,
      leagueName: leagueAttr?.name ?? '',
      player: {
        imageUrl: playerAttr?.image_url ?? PLAYER_PLACEHOLDER_IMAGE_URL,
        name: playerAttr?.name ?? '',
        position: playerAttr?.position ?? '',
        team: playerAttr?.team ?? '',
        teamName: playerAttr?.team_name ?? '',
        status: playerStatus
      },
      predictionStats: {
        lineScore: predictionAttr?.line_score ?? '',
        oddsType: predictionAttr?.odds_type ?? 'standard'
      },
      projectionStats: {
        discountName: projectionAttr?.discount_name ?? null,
        flashSaleLineScore: projectionAttr?.flash_sale_line_score ?? null,
        startTime: projectionAttr?.start_time ?? ''
      },
      scoreStats: {
        isFinal: scoreAttr?.is_final ?? false,
        isOffTheBoard: scoreAttr?.is_off_the_board ?? false,
        score: scoreAttr?.score
      },
      statType: {
        name: statTypeAttr?.name ?? ''
      },
      wagerType
    };
  });

  return lineup || [];
};

export const getNewWagerPromotion = (
  promotionData: NewWagerIdType | null,
  included: Array<NewWagerIncluded>
): FormattedNewWagerPromotion => {
  const promo = included?.find(
    ({ id, type }) => id === promotionData?.id && type === 'promotion'
  )?.attributes;

  return {
    id: promo?.id ?? '',
    name: promo?.name ?? '',
    type: promo?.type ?? null
  };
};

export const getNewWagerDetails = (
  newWager: NewWager,
  included: Array<NewWagerIncluded>
): FormattedNewWagerAttributes => {
  const { attributes, id, relationships } = newWager;

  const {
    amount_bet_cents: amountBetCents,
    amount_to_win_cents: amountToWinCents,
    amount_won_cents: amountWonCents,
    contest_amount_won_cents: contestAmountWonCents,
    contest_id: contestId,
    correlated_entry: correlatedEntry,
    created_at: createdAt,
    description,
    eligible_for_refund: eligibleForRefund,
    f2p,
    free_entry: freeEntry,
    location,
    payout_data: payoutData,
    payout_multipliers: payoutMultipliers,
    pick_protection: pickProtection,
    refund_allowed_until: refundAllowedUntil,
    self_refunded_at: selfRefundedAt,
    result
  } = attributes;

  const gameModeId = relationships?.game_mode?.data?.id;
  const gameMode = included.find(
    ({ id: gmId, type }) => gmId === gameModeId && type === 'game_mode'
  )?.attributes?.name;
  const lineupGameType = getNewWagerLineupGameType(gameMode, pickProtection);

  return {
    amountBetCents,
    amountToWinCents,
    amountWonCents,
    contestAmountWonCents,
    contestId,
    correlatedEntry,
    createdAt,
    description,
    eligibleForRefund,
    f2p,
    freeEntry,
    gameMode,
    id,
    lineupGameType,
    location,
    payoutData,
    payoutMultipliers,
    pickProtection,
    refundAllowedUntil,
    result,
    selfRefundedAt
  };
};

export const transformNewWagers = (
  response: NewWagersResponse | undefined,
  page: number = 1,
  perPage: number = 10
): FormattedNewWagersResponse => {
  if (!response) {
    return {
      count: 0,
      data: [],
      estimatedTotal: 0
    };
  }

  const { data, included } = response;
  const totalPages = data?.length < perPage ? page : page + 1;

  // Map new wager data from data, relationships, and included
  const newWagers: FormattedNewWagers =
    data?.map((newWagerData) => {
      const { relationships } = newWagerData;
      const { predictions, promotion: promo } = relationships;

      const lineup = getNewWagerLineup(predictions?.data, included);
      const newWager = getNewWagerDetails(newWagerData, included);
      const promotion = getNewWagerPromotion(promo?.data, included);

      return {
        lineup,
        newWager,
        promotion
      };
    }) || [];

  const formattedResponse = {
    count: page * perPage,
    data: newWagers,
    estimatedTotal: totalPages * perPage
  };

  return formattedResponse;
};

export const newWagerResultColor = (result: NewWagerResultStatus): string => {
  switch (result) {
    case 'pending':
      return 'bg-status-caution';
    case 'won':
    case 'partial_win':
    case 'contest_win':
      return 'bg-status-success';
    case 'lost':
    case 'contest_lost':
      return 'bg-status-info';
    case 'admin_refunded':
    case 'refunded':
    case 'canceled':
      return 'bg-status-failure';
    default: // voided_mi, no_op, dnp
      return 'bg-soFresh-110';
  }
};

export const newWagerResultHeader = (
  lineupAmount: number | undefined,
  newWager: FormattedNewWagerAttributes
): FormattedNewWagerHeader => {
  const { amountToWinCents, amountWonCents, contestAmountWonCents, result } =
    newWager;
  const amountToWin = convertCentsToDollars(amountToWinCents);
  // Calculate total amount won including contest winnings
  const amountWon = amountWonCents + contestAmountWonCents;
  const totalAmountWon = convertCentsToDollars(amountWon);

  const isWin = ['contest_win', 'partial_win', 'won'].includes(result);
  const isLossCancelOrRefund = [
    'canceled',
    'contest_lost',
    'lost',
    'no_op',
    'refunded'
  ].includes(result);

  // Default values for pending status
  let color = 'text-soClean-100';
  let resultAmount = amountToWin;
  let text = `${lineupAmount}-Pick to pay`;

  if (isWin) {
    color = 'text-atlien-100';
    resultAmount = totalAmountWon;
    text = `${lineupAmount}-Pick paid`;
  } else if (isLossCancelOrRefund) {
    color = 'text-soClean-180';
    text = `${lineupAmount}-Pick for`;
  }

  return {
    color,
    resultAmount,
    text
  };
};

const formatNewWagerResultText = (result: NewWagerResultStatus): string => {
  switch (result) {
    case 'won':
      return 'win';
    case 'lost':
      return 'loss';
    case 'no_op':
      return 'dnp';
    case 'pushed':
      return 'push';
    case 'refunded':
      return 'refund';
    case 'admin_refunded':
      return 'admin refund';
    case 'voided_mi':
      return 'void';
    default:
      return result.replace(/_/g, ' ');
  }
};

const formatNewWagerGameMode = (gameMode: string): string => {
  switch (gameMode) {
    case 'f2p_streak':
      return 'Streak';
    case 'pickem':
      return "Pick 'Em";
    case 'prizepools':
      return "Pick 'Em Arena";
    case 'squads':
      return 'Squad';
    default:
      return removeUnderscore(gameMode);
  }
};

export const lineupPlayerStatusDisplay = (
  lineup: FormattedNewWagerLineupDetails
): NewWagerPlayerStatusDisplay => {
  const { player, projectionStats, scoreStats, wagerType } = lineup;
  const { status } = player;
  const { startTime } = projectionStats;
  const { isFinal, isOffTheBoard } = scoreStats;
  const isGameLive = isNewWagerGameLive(
    startTime,
    status,
    isFinal,
    isOffTheBoard
  );

  const defaultStatusDisplay: NewWagerPlayerStatusDisplay = {
    borderColor: 'outline-soClean-180',
    label: 'Final',
    statusBadge: null
  };
  const statusDisplay = { ...defaultStatusDisplay };

  // Based on player status within prizepicks-fe repo
  switch (true) {
    case isGameLive && wagerType === 'over' && status === 'hitting':
      statusDisplay.borderColor = 'outline-atlien-100';
      statusDisplay.label = 'Hitting';
      break;
    case isGameLive && wagerType === 'under' && status === 'missing':
      statusDisplay.borderColor = 'outline-roses';
      statusDisplay.label = 'Missing';
      break;
    case isGameLive:
      statusDisplay.borderColor = 'outline-soClean-100';
      statusDisplay.label = 'In Progress';
      statusDisplay.statusBadge = null;
      break;
    case status === 'dnp':
      statusDisplay.label = 'DNP';
      statusDisplay.statusBadge = dnpBadge;
      break;
    case status === 'hit':
      statusDisplay.borderColor = 'outline-atlien-100';
      statusDisplay.statusBadge = hitBadge;
      break;
    case status === 'in_progress':
      statusDisplay.borderColor = 'outline-soClean-100';
      statusDisplay.label = 'In Progress';
      break;
    case status === 'miss':
      statusDisplay.borderColor = 'outline-roses';
      statusDisplay.statusBadge = missBadge;
      break;
    case status === 'reboot':
      statusDisplay.statusBadge = rebootBadge;
      break;
    case status === 'tie':
      statusDisplay.statusBadge = tieBadge;
      break;
    case status === 'tying':
      statusDisplay.statusBadge = null;
      statusDisplay.label = 'Tying';
      break;
    default: // pre_game
      statusDisplay.borderColor = 'outline-soFresh-140';
      statusDisplay.label = 'Pre Game';
      statusDisplay.statusBadge = notStartedBadge;
      break;
  }

  return statusDisplay;
};

export const formattedNewWagerDetails = (
  newWager: FormattedNewWagerAttributes,
  lineup?: FormattedNewWagerLineup
) => {
  const {
    amountBetCents,
    amountToWinCents,
    amountWonCents,
    createdAt,
    gameMode,
    result,
    selfRefundedAt
  } = newWager;

  const amountBet = convertCentsToDollars(amountBetCents);
  const amountToWin = convertCentsToDollars(amountToWinCents);
  const amountWon = convertCentsToDollars(amountWonCents);

  const resultBgColor = newWagerResultColor(result);
  const resultTextColor =
    resultBgColor !== 'bg-soFresh-110'
      ? 'text-soFresh-100'
      : 'text-soClean-100';
  const resultColor = `${resultBgColor} ${resultTextColor}`;

  const resultHeader = newWagerResultHeader(lineup?.length, newWager);

  const formattedResult = formatNewWagerResultText(result);

  const formattedGameMode = formatNewWagerGameMode(gameMode ?? '');

  const { origin, pathname } = window.location;
  const { entries } = MEMBER_DETAILS_CHILD_PATHS;
  const removeLastPath = (url: string) => url.replace(/\/[^/]*$/, '');
  const newWagerIdUrl = `${removeLastPath(origin + pathname)}/${entries}?entryId=${newWager.id}`;

  const newWagerCreatedDate = dateDisplay(createdAt, true);

  const selfRefundDate = selfRefundedAt
    ? DateTime.fromISO(selfRefundedAt).toFormat(DATE_TIME_FORMAT)
    : null;

  let formattedDescriptions: Array<string> = [];

  if (lineup) {
    const lineupPlayerNames = lineup.map(({ player }) =>
      // Escape '+' character in player names for regex
      player.name.replace(/\+/g, '\\+')
    );
    const regex = new RegExp(`(?=${lineupPlayerNames.join('|')})`, 'g');
    formattedDescriptions = newWager.description
      .split(regex)
      .filter(Boolean)
      .map((str) => str.trim());
  }

  return {
    amountBet,
    amountToWin,
    amountWon,
    formattedDescriptions,
    formattedGameMode,
    formattedResult,
    newWagerCreatedDate,
    newWagerIdUrl,
    resultColor,
    resultHeader,
    selfRefundDate
  };
};

export const formatPromotionType = (
  promoType: NewWagerPromotionType | null
): string => {
  if (!promoType) {
    return '';
  }

  // Convert pascal case to space separated string
  return promoType.replace(/([a-z])([A-Z])/g, '$1 $2');
};

export const getNewWagersModalDetails = (
  newWagerDetails: FormattedNewWager
): Array<NewWagerModalDetails> => {
  const { newWager, promotion } = newWagerDetails;
  const {
    amountBet,
    amountWon,
    formattedGameMode,
    formattedResult,
    selfRefundDate
  } = formattedNewWagerDetails(newWager);

  const wagerModalValues = {
    amountBet,
    amountWon,
    correlated: newWager.correlatedEntry ? 'Yes' : 'No',
    f2p: newWager.f2p ? 'Yes' : 'No',
    flexPower: newWager.pickProtection ? 'Flex' : 'Power',
    freeEntry: newWager.freeEntry ? 'Yes' : 'No',
    gameType: formattedGameMode,
    location: String(newWager.location),
    payoutData: newWager.payoutData
      ? JSON.stringify(newWager.payoutData)
      : 'N/A',
    payoutMultipliers: newWager.payoutMultipliers,
    promoType: formatPromotionType(promotion.type),
    promoId: String(promotion.id),
    result: formattedResult,
    selfRefundedAt: selfRefundDate
  };

  const wagerModalDetails = NEW_WAGER_MODAL_DETAILS.map(
    ({ containerClassName, key, label, valueClassName }) => {
      const value = wagerModalValues[key];

      return {
        containerClassName,
        key,
        label,
        value,
        valueClassName
      };
    }
  ) as Array<NewWagerModalDetails>;

  return wagerModalDetails;
};

export const getNewWagerById = (
  newWagers: FormattedNewWagers | undefined,
  newWagerId: string
): FormattedNewWager | undefined => {
  if (!newWagers) {
    return undefined;
  }

  return newWagers.find(({ newWager: { id } }) => id === newWagerId);
};

export const newWagerDateRangeRequest = (
  dateRange: NewWagerDateRangeFilter
) => {
  const { startDate, endDate } = dateRange;
  const dateFormat = 'yyyy-MM-dd HH:mm:ss';

  const formattedStartDate = startDate?.startOf('day').toFormat(dateFormat);
  const formattedEndDate = endDate?.endOf('day').toFormat(dateFormat);

  return { formattedStartDate, formattedEndDate };
};

export const formatNewWagerRefundError = (
  error?: string
): string | undefined => {
  if (!error) {
    return undefined;
  }

  const formattedError = error
    ?.replace(/,\s*/g, '.\n')
    .replace(/and New wager /g, '• ')
    .replace(/New wager /g, '• ');

  const errorMessage = `Unable to refund entry due to:\n${formattedError}`;

  return errorMessage;
};
